import {
  ComponentIds,
  Entity,
  Transform,
  TransformUtils,
  Vector,
  ViewerDragEventComponent
} from '@inter-ikea-core/core';

/* Since ViewerDragEventComponent does not seem to respect the delta vector,
just pass a zero sized vector for no delta. */
const ZERO_DELTA = new Vector(0, 0, 0);

/**
 * Moves entity in desired direction according to camera transform.
 * That is, move it in what the user is consering to be the direction
 * according to the screen viewed, not what the entity consider is the direction.
 * i.e. right is to the right on the screen, not to the right of the entity.
 *
 * Implementation is based on "simulating" pointer drags via the ViewerDragEventComponent,
 * to ensure same behaviour between pointer device and command based movements.
 */
export function moveEntity(
  entity: Entity,
  step: number,
  direction: Vector,
  cameraTransform: Transform
): void {
  const entityTransform = TransformUtils.worldTransform(entity);
  const entityPosition = entityTransform.position;
  const cameraPosition = cameraTransform.position;

  const movementStep = direction.times(step);

  // Normal facing camera from entity to move
  const planeNormal = cameraPosition.minus(entityPosition).normalize();

  const startDragComponent = new ViewerDragEventComponent(
    entityPosition,
    planeNormal,
    ZERO_DELTA,
    cameraPosition
  );

  entity.addComponent(startDragComponent);

  let delta = movementStep;
  let tries = 0;

  while (tries <= 100) {
    const moveEvent = new ViewerDragEventComponent(
      entityPosition.plus(delta), // For some reason, supplying delta gives no effect, so update the position instead.
      planeNormal,
      ZERO_DELTA,
      cameraPosition
    );

    entity.addComponent(moveEvent);

    const hasMoved = !entityTransform.equals(TransformUtils.worldTransform(entity));

    if (hasMoved) {
      break;
    } else {
      delta = delta.plus(movementStep);
      tries++;
    }
  }

  entity.removeComponent(ComponentIds.VIEWER_DRAG_EVENT_COMPONENT_ID);
}
